/** Yorick spoken replies. Volume is a fraction of the device level — never 100%. */

export const VOICE_STORAGE_KEY = "gp_yorick_voice";
export const GREETING_STORAGE_KEY = "gp_yorick_greeted";
export const DEFAULT_VOICE_VOLUME = 0.45;
export const MIN_VOICE_VOLUME = 0.12;
export const MAX_VOICE_VOLUME = 0.72;

export const YORICK_GREETING_SPEECH =
  "Alas, a challenger approaches. I'm Yorick, your strategy coach. Pick a game, or just ask. How can I help you win?";

export const YORICK_VOICE_SAMPLE =
  "Mm. That's my voice. Warm, a little raspy, not too high. I'm Yorick. How can I help you win?";

let unlocked = false;
let chromeWatch = null;
let speakTimer = null;
let gapTimer = null;

function speech() {
  return typeof window !== "undefined" ? window.speechSynthesis : null;
}

export function voiceSupported() {
  return Boolean(speech() && typeof window.SpeechSynthesisUtterance === "function");
}

export function clampVoiceVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_VOICE_VOLUME;
  return Math.min(MAX_VOICE_VOLUME, Math.max(MIN_VOICE_VOLUME, n));
}

export function loadVoicePrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(VOICE_STORAGE_KEY) || "{}");
    return {
      muted: Boolean(raw.muted),
      volume: clampVoiceVolume(raw.volume ?? DEFAULT_VOICE_VOLUME),
    };
  } catch {
    return { muted: false, volume: DEFAULT_VOICE_VOLUME };
  }
}

export function saveVoicePrefs(prefs) {
  try {
    localStorage.setItem(VOICE_STORAGE_KEY, JSON.stringify({
      muted: Boolean(prefs.muted),
      volume: clampVoiceVolume(prefs.volume),
    }));
  } catch {
    /* private mode / blocked storage */
  }
}

export function greetingAlreadyPlayed() {
  try {
    return sessionStorage.getItem(GREETING_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markGreetingPlayed() {
  try {
    sessionStorage.setItem(GREETING_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function speakableText(markdown) {
  return String(markdown || "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*[-*•]\s+/gm, "")
    .replace(/^\s*\d+[.)]\s+/gm, "")
    .replace(/[#*_>~]/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+\n/g, "\n")
    .replace(/\n{2,}/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

const WARM_VOICE = [
  /google uk english male/i,
  /\bdaniel\b/i,
  /\barthur\b/i,
  /\bguy\b/i,
  /\bryan\b/i,
  /\breed\b/i,
  /\baaron\b/i,
  /\bmaurice\b/i,
  /\bmalcolm\b/i,
  /\bnathan\b/i,
  /\boliver\b/i,
  /\bthomas\b/i,
  /\bgeorge\b/i,
  /\bjames\b/i,
  /\balex\b/i,
  /\bmicrosoft (david|mark)/i,
  /english male/i,
  /\bmale\b/i,
];

const AVOID_VOICE = /fred|whisper|zarvox|trinoids|bells|boing|bubbles|cellos|bad news|good news|jester|organ|superstar|kathy|princess|samantha|karen|moira|tessa|veena|fiona|victoria|zira|hazel|susan|siri|grandma|grandpa|novelty/i;

function scoreVoice(voice) {
  const name = `${voice.name || ""} ${voice.lang || ""}`;
  if (AVOID_VOICE.test(name)) return -100;
  let score = 0;
  if (/^en(-|_)/i.test(voice.lang || "")) score += 8;
  if (/en-GB/i.test(voice.lang || "")) score += 4;
  if (voice.localService) score += 2;
  WARM_VOICE.forEach((re, i) => {
    if (re.test(name)) score += 20 - i;
  });
  return score;
}

function pickYorickVoice() {
  const synth = speech();
  if (!synth) return null;
  const voices = synth.getVoices() || [];
  if (!voices.length) return null;
  const ranked = [...voices].sort((a, b) => scoreVoice(b) - scoreVoice(a));
  return ranked[0] || null;
}

function phraseChunks(text, max = 180) {
  const phrases = String(text || "")
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const parts = [];
  phrases.forEach((phrase) => {
    if (phrase.length <= max) {
      parts.push(phrase);
      return;
    }
    let rest = phrase;
    while (rest.length > max) {
      const slice = rest.slice(0, max);
      const cut = Math.max(slice.lastIndexOf(", "), slice.lastIndexOf(" "));
      const at = cut > 40 ? cut + 1 : max;
      parts.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest) parts.push(rest);
  });
  return parts;
}

function inflectionFor(text, index, style) {
  const greeting = style === "greeting";
  const contour = greeting
    ? [
        { pitch: 0.76, rate: 0.86 },
        { pitch: 0.84, rate: 0.9 },
        { pitch: 0.72, rate: 0.84 },
        { pitch: 0.8, rate: 0.88 },
      ]
    : [
        { pitch: 0.78, rate: 0.9 },
        { pitch: 0.86, rate: 0.94 },
        { pitch: 0.74, rate: 0.88 },
        { pitch: 0.82, rate: 0.92 },
      ];
  const base = contour[index % contour.length];
  const question = /\?\s*$/.test(text);
  return {
    pitch: Math.min(1.02, base.pitch + (question ? 0.07 : 0)),
    rate: base.rate + (question ? 0.03 : 0),
  };
}

function clearTimers() {
  if (chromeWatch) {
    clearInterval(chromeWatch);
    chromeWatch = null;
  }
  if (speakTimer) {
    clearTimeout(speakTimer);
    speakTimer = null;
  }
  if (gapTimer) {
    clearTimeout(gapTimer);
    gapTimer = null;
  }
}

export function stopYorickVoice() {
  const synth = speech();
  clearTimers();
  if (!synth) return;
  synth.cancel();
}

export function unlockYorickVoice() {
  const synth = speech();
  if (!synth || unlocked) return;
  try {
    const warm = new window.SpeechSynthesisUtterance(" ");
    warm.volume = 0.01;
    warm.rate = 1;
    synth.speak(warm);
    synth.cancel();
    unlocked = true;
  } catch {
    unlocked = true;
  }
}

export function speakYorick(text, { volume = DEFAULT_VOICE_VOLUME, muted = false, style = "reply", onStart } = {}) {
  const synth = speech();
  if (!synth || muted) {
    stopYorickVoice();
    return;
  }
  const spoken = speakableText(text);
  if (!spoken) return;

  stopYorickVoice();
  const level = clampVoiceVolume(style === "greeting" ? volume * 0.88 : volume);
  const voice = pickYorickVoice();
  const chunks = phraseChunks(spoken);
  const gap = style === "greeting" ? 260 : 90;
  let started = false;
  let index = 0;

  speakTimer = window.setTimeout(() => {
    const speakNext = () => {
      const next = chunks.shift();
      if (!next) {
        clearTimers();
        return;
      }
      const shape = inflectionFor(next, index, style);
      index += 1;
      const utterance = new window.SpeechSynthesisUtterance(next);
      utterance.volume = level;
      utterance.rate = shape.rate;
      utterance.pitch = shape.pitch;
      if (voice) utterance.voice = voice;
      utterance.onstart = () => {
        if (!started) {
          started = true;
          onStart?.();
        }
      };
      utterance.onend = () => {
        gapTimer = window.setTimeout(speakNext, chunks.length ? gap : 0);
      };
      utterance.onerror = () => speakNext();
      synth.speak(utterance);
    };

    speakNext();
    chromeWatch = setInterval(() => {
      if (!synth.speaking) {
        clearInterval(chromeWatch);
        chromeWatch = null;
        return;
      }
      synth.pause();
      synth.resume();
    }, 10000);
  }, 50);
}
